(() => {
  if (customElements.get("before-after")) return;

  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      if (this.dataset.beforeAfterInit === "true") {
        return;
      }

      this.dataset.beforeAfterInit = "true";

      const container = this.querySelector("[data-before-after-container]");
      const beforeLayer = this.querySelector("[data-before-after-before]");
      const handle = this.querySelector("[data-before-after-handle]");
      const handleButton = handle?.querySelector(
        "[data-before-after-handle-button]",
      );
      const labels = this.querySelectorAll("[data-before-after-label]");
      const beforeLabel = this.querySelector(
        '[data-before-after-label="before"]',
      );
      const afterLabel = this.querySelector(
        '[data-before-after-label="after"]',
      );

      if (!container || !beforeLayer || !handle || !handleButton) {
        return;
      }
      this.__beforeAfterCleanup = [];
      const addListener = (el, event, handler, options) => {
        if (!el) return;
        el.addEventListener(event, handler, options);
        this.__beforeAfterCleanup.push(() =>
          el.removeEventListener(event, handler, options),
        );
      };

      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      const start = 10;
      const target = 50;
      const direction =
        this.dataset.beforeAfterDirection === "vertical"
          ? "vertical"
          : "horizontal";
      const isRtl = document.documentElement.getAttribute("dir") === "rtl";
      const state = { dragging: false, value: clamp(start, 0, 100) };
      const supportsClipPath =
        CSS.supports?.("clip-path", "inset(0 0 0 0)") === true;

      const resetLabelBlockStyles = (label) => {
        if (!label) return;
        label.style.clipPath = "";
        label.style.width = "";
        label.style.height = "";
        label.style.overflow = "";
      };

      const hideLabelWithSize = (label, axis, sizePx) => {
        if (!label) return;
        if (axis === "width") {
          label.style.width = sizePx;
        } else {
          label.style.height = sizePx;
        }
        label.style.overflow = "hidden";
      };

      const clearFocusHideState = () => {
        delete handleButton.dataset.hideFocus;
      };

      const updateLabelClip = (value) => {
        if (!beforeLabel && !afterLabel) return;

        const containerRect = container.getBoundingClientRect();

        if (direction === "vertical") {
          const handlePos = containerRect.height * (value / 100);

          if (beforeLabel) {
            const labelRect = beforeLabel.getBoundingClientRect();
            const labelTop = labelRect.top - containerRect.top;
            const labelBottom = labelRect.bottom - containerRect.top;
            const labelHeight = labelRect.height || 1;

            if (handlePos <= labelTop) {
              if (supportsClipPath) {
                beforeLabel.style.clipPath = "inset(0 0 100% 0)";
              } else {
                hideLabelWithSize(beforeLabel, "height", "0px");
              }
            } else if (handlePos >= labelBottom) {
              resetLabelBlockStyles(beforeLabel);
            } else {
              const visible = Math.max(handlePos - labelTop, 0);
              const visiblePercent = (visible / labelHeight) * 100;
              if (supportsClipPath) {
                beforeLabel.style.clipPath = `inset(0 0 ${100 - visiblePercent}% 0)`;
              } else {
                hideLabelWithSize(beforeLabel, "height", `${visible}px`);
              }
            }
          }

          if (afterLabel) {
            const labelRect = afterLabel.getBoundingClientRect();
            const labelTop = labelRect.top - containerRect.top;
            const labelBottom = labelRect.bottom - containerRect.top;
            const labelHeight = labelRect.height || 1;

            if (handlePos <= labelTop) {
              resetLabelBlockStyles(afterLabel);
            } else if (handlePos >= labelBottom) {
              if (supportsClipPath) {
                afterLabel.style.clipPath = "inset(0 0 100% 0)";
              } else {
                hideLabelWithSize(afterLabel, "height", "0px");
              }
            } else {
              const visible = Math.max(labelBottom - handlePos, 0);
              const visiblePercent = (visible / labelHeight) * 100;
              if (supportsClipPath) {
                afterLabel.style.clipPath = `inset(${100 - visiblePercent}% 0 0 0)`;
              } else {
                hideLabelWithSize(afterLabel, "height", `${visible}px`);
              }
            }
          }

          return;
        }

        const handlePos = containerRect.width * (value / 100);

        if (beforeLabel) {
          const labelRect = beforeLabel.getBoundingClientRect();
          const labelLeft = labelRect.left - containerRect.left;
          const labelRight = labelRect.right - containerRect.left;
          const labelWidth = labelRect.width || 1;

          if (isRtl) {
            if (handlePos <= labelLeft) {
              resetLabelBlockStyles(beforeLabel);
            } else if (handlePos >= labelRight) {
              if (supportsClipPath) {
                beforeLabel.style.clipPath = "inset(0 0 0 100%)";
                beforeLabel.style.width = "";
                beforeLabel.style.overflow = "";
              } else {
                hideLabelWithSize(beforeLabel, "width", "0px");
              }
            } else {
              const visible = Math.max(labelRight - handlePos, 0);
              const visiblePercent = (visible / labelWidth) * 100;
              if (supportsClipPath) {
                beforeLabel.style.clipPath = `inset(0 0 0 ${100 - visiblePercent}%)`;
                beforeLabel.style.width = "";
                beforeLabel.style.overflow = "";
              } else {
                hideLabelWithSize(beforeLabel, "width", `${visible}px`);
              }
            }
          } else if (handlePos >= labelRight) {
            resetLabelBlockStyles(beforeLabel);
          } else if (handlePos <= labelLeft) {
            if (supportsClipPath) {
              beforeLabel.style.clipPath = "inset(0 100% 0 0)";
              beforeLabel.style.width = "";
              beforeLabel.style.overflow = "";
            } else {
              hideLabelWithSize(beforeLabel, "width", "0px");
            }
          } else {
            const visible = Math.max(handlePos - labelLeft, 0);
            const visiblePercent = (visible / labelWidth) * 100;
            if (supportsClipPath) {
              beforeLabel.style.clipPath = `inset(0 ${100 - visiblePercent}% 0 0)`;
              beforeLabel.style.width = "";
              beforeLabel.style.overflow = "";
            } else {
              hideLabelWithSize(beforeLabel, "width", `${visible}px`);
            }
          }
        }

        if (afterLabel) {
          const labelRect = afterLabel.getBoundingClientRect();
          const labelLeft = labelRect.left - containerRect.left;
          const labelRight = labelRect.right - containerRect.left;
          const labelWidth = labelRect.width || 1;

          if (isRtl) {
            if (handlePos >= labelRight) {
              resetLabelBlockStyles(afterLabel);
            } else if (handlePos <= labelLeft) {
              if (supportsClipPath) {
                afterLabel.style.clipPath = "inset(0 100% 0 0)";
                afterLabel.style.width = "";
                afterLabel.style.overflow = "";
              } else {
                hideLabelWithSize(afterLabel, "width", "0px");
              }
            } else {
              const visible = Math.max(handlePos - labelLeft, 0);
              const visiblePercent = (visible / labelWidth) * 100;
              if (supportsClipPath) {
                afterLabel.style.clipPath = `inset(0 ${100 - visiblePercent}% 0 0)`;
                afterLabel.style.width = "";
                afterLabel.style.overflow = "";
              } else {
                hideLabelWithSize(afterLabel, "width", `${visible}px`);
              }
            }
          } else if (handlePos <= labelLeft) {
            resetLabelBlockStyles(afterLabel);
          } else if (handlePos >= labelRight) {
            if (supportsClipPath) {
              afterLabel.style.clipPath = "inset(0 0 0 100%)";
              afterLabel.style.width = "";
              afterLabel.style.overflow = "";
            } else {
              hideLabelWithSize(afterLabel, "width", "0px");
            }
          } else {
            const visible = Math.max(labelRight - handlePos, 0);
            const visiblePercent = (visible / labelWidth) * 100;
            if (supportsClipPath) {
              afterLabel.style.clipPath = `inset(0 0 0 ${100 - visiblePercent}%)`;
              afterLabel.style.width = "";
              afterLabel.style.overflow = "";
            } else {
              hideLabelWithSize(afterLabel, "width", `${visible}px`);
            }
          }
        }
      };

      const enableTransition = () => {
        container.classList.add("enable-transition");
      };

      const disableTransition = () => {
        container.classList.remove("enable-transition");
      };

      const update = (value) => {
        const next = clamp(value, 0, 100);
        state.value = next;
        handle.style.setProperty("--before-after-position", `${next}%`);
        if (direction === "vertical") {
          if (supportsClipPath) {
            beforeLayer.style.clipPath = `inset(0 0 ${100 - next}% 0)`;
          } else {
            beforeLayer.style.clip = `rect(0px, ${container.offsetWidth}px, ${container.offsetHeight * (next / 100)}px, 0px)`;
          }
        } else {
          if (supportsClipPath) {
            beforeLayer.style.clipPath = isRtl
              ? `inset(0 0 0 ${next}%)`
              : `inset(0 ${100 - next}% 0 0)`;
          } else {
            beforeLayer.style.clip = isRtl
              ? `rect(0px, ${container.offsetWidth}px, ${container.offsetHeight}px, ${container.offsetWidth * (next / 100)}px)`
              : `rect(0px, ${container.offsetWidth * (next / 100)}px, ${container.offsetHeight}px, 0px)`;
          }
        }
        handleButton.setAttribute("aria-valuenow", Math.round(next).toString());
        updateLabelClip(next);
      };

      const getPercentage = (clientX, clientY) => {
        const rect = container.getBoundingClientRect();
        if (direction === "vertical") {
          const offsetY = clamp(clientY - rect.top, 0, rect.height);
          return (offsetY / rect.height) * 100;
        }
        const offsetX = clamp(clientX - rect.left, 0, rect.width);
        return (offsetX / rect.width) * 100;
      };

      const onPointerMove = (event) => {
        if (!state.dragging) {
          return;
        }

        update(getPercentage(event.clientX, event.clientY));
      };

      const stopDrag = (event) => {
        if (!state.dragging) {
          return;
        }
        if (
          event &&
          event.pointerId !== undefined &&
          state.pointerId !== undefined &&
          event.pointerId !== state.pointerId
        ) {
          return;
        }

        state.dragging = false;
        if (state.pointerId !== undefined) {
          try {
            handle.releasePointerCapture?.(state.pointerId);
          } catch (error) {
            // pointer capture may already be released
          }
        }
        state.pointerId = undefined;
        labels.forEach((label) => {
          label.classList.remove("opacity-0");
          label.classList.remove("pointer-events-none");
        });
        handleButton.dataset.hideFocus = "true";
        updateLabelClip(state.value);
      };

      const cancelDrag = () => {
        if (!state.dragging) {
          return;
        }
        stopDrag();
      };

      const onPointerDown = (event) => {
        if (!event.isPrimary || event.button !== 0) {
          return;
        }
        event.preventDefault();
        state.dragging = true;
        state.pointerId = event.pointerId;
        handle.setPointerCapture?.(event.pointerId);
        handleButton.dataset.hideFocus = "true";
        handleButton.focus?.({ preventScroll: true });
        labels.forEach((label) => {
          label.classList.add("opacity-0");
          label.classList.add("pointer-events-none");
        });
        update(getPercentage(event.clientX, event.clientY));
      };

      addListener(handle, "pointerdown", onPointerDown);
      addListener(handle, "pointermove", onPointerMove);
      addListener(handle, "pointerup", stopDrag);
      addListener(handle, "pointercancel", stopDrag);
      addListener(handle, "lostpointercapture", stopDrag);
      addListener(window, "pointerup", stopDrag);
      addListener(window, "pointercancel", stopDrag);
      addListener(window, "blur", cancelDrag);
      addListener(document, "visibilitychange", () => {
        if (document.hidden) {
          cancelDrag();
        }
      });
      addListener(handleButton, "blur", clearFocusHideState);

      const onKeydown = (event) => {
        const step = 2;
        let nextValue = state.value;

        clearFocusHideState();

        if (direction === "vertical") {
          if (event.key === "ArrowUp") {
            nextValue -= step;
          } else if (event.key === "ArrowDown") {
            nextValue += step;
          } else if (event.key === "Home") {
            nextValue = 0;
          } else if (event.key === "End") {
            nextValue = 100;
          } else {
            return;
          }
        } else {
          if (event.key === "ArrowLeft") {
            nextValue += isRtl ? step : -step;
          } else if (event.key === "ArrowRight") {
            nextValue += isRtl ? -step : step;
          } else if (event.key === "Home") {
            nextValue = 0;
          } else if (event.key === "End") {
            nextValue = 100;
          } else {
            return;
          }
        }

        event.preventDefault();
        update(nextValue);
      };
      addListener(handleButton, "keydown", onKeydown);

      const onResize = () => {
        if (this.__beforeAfterResizeRaf) {
          cancelAnimationFrame(this.__beforeAfterResizeRaf);
        }
        this.__beforeAfterResizeRaf = requestAnimationFrame(() => {
          update(state.value);
        });
      };
      addListener(window, "resize", onResize);

      update(state.value);

      const playIntro = () => {
        if (this.__beforeAfterIntroPlayed) return;
        this.__beforeAfterIntroPlayed = true;
        enableTransition();
        update(target);
        setTimeout(disableTransition, 500);
      };

      const observer = new IntersectionObserver(
        (entries, io) => {
          if (!entries[0]?.isIntersecting) return;
          playIntro();
          io.disconnect();
        },
        { rootMargin: "-150px 0px -150px 0px" },
      );
      observer.observe(this);
      this.__beforeAfterObserver = observer;
    }

    disconnectedCallback() {
      if (this.__beforeAfterCleanup?.length) {
        this.__beforeAfterCleanup.forEach((remove) => remove());
        this.__beforeAfterCleanup = [];
      }
      if (this.__beforeAfterResizeRaf) {
        cancelAnimationFrame(this.__beforeAfterResizeRaf);
        this.__beforeAfterResizeRaf = null;
      }
      if (this.__beforeAfterObserver) {
        this.__beforeAfterObserver.disconnect();
        this.__beforeAfterObserver = null;
      }
      delete this.dataset.beforeAfterInit;
    }
  }

  customElements.define("before-after", BeforeAfter);
})();
